import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { Alert, Card, Spinner, useToast } from '@platform/ui';

import { DomainsTab } from '../../../features/tenants/domains-tab';
import { useRefreshTenant, useTenantDetail } from '../../../features/tenants/use-tenant';
import type { DomainAppKind } from '../../../lib/api/platform';
import { usePlatformClient } from '../../../lib/api/platform-client-context';
import { useStepUp } from '../../../lib/session/step-up';

export const Route = createFileRoute('/tenants/$tenantId/domains')({
  component: TenantDomainsPage,
});

/**
 * `/tenants/$id/domains` (FR-PLAT-02).
 *
 * The platform domain and the CNAME target come from `GET /platform/settings`, where the
 * API reads them from its own `PLATFORM_DOMAIN` at boot: a value that disagreed with the
 * process's own would be a hostname the API does not answer on, displayed as though it
 * did (API.md §2.13).
 *
 * The hostnames are their own read (`GET /platform/tenants/:id/domains`) rather than the
 * tenant detail's `domains`, because only this endpoint carries the CNAME target and the
 * TXT record, and those two records are the whole screen.
 *
 * **Remove is step-up gated** (`DELETE …/domains/:domainId`, API.md §2.13), so it goes
 * through `useStepUp().guard`: the refusal is caught, a second factor is asked for, and
 * the same delete runs again - the operator does not confirm the dialog twice.
 */
function TenantDomainsPage() {
  const { tenantId } = Route.useParams();
  const client = usePlatformClient();
  const refresh = useRefreshTenant(tenantId);
  const { guard } = useStepUp();
  const { toast } = useToast();
  const tenant = useTenantDetail(tenantId);
  const [error, setError] = useState<string | undefined>(undefined);
  const [busyDomainId, setBusyDomainId] = useState<string | null>(null);

  const domains = useQuery({
    queryKey: ['platform-tenant-domains', tenantId],
    queryFn: () => client.listDomains(tenantId),
  });
  const settings = useQuery({
    queryKey: ['platform-settings'],
    queryFn: () => client.getSettings(),
  });

  const add = useMutation({
    mutationFn: (input: { host: string; appKind: DomainAppKind }) =>
      client.addDomain(tenantId, input),
    onSuccess: async (domain) => {
      setError(undefined);
      toast({
        tone: 'success',
        message: `${domain.host} added. It serves no traffic until both DNS records resolve.`,
      });
      await refresh();
    },
    onError: (cause: Error) => {
      setError(cause.message);
    },
  });

  const verify = useMutation({
    mutationFn: (domainId: string) => {
      setBusyDomainId(domainId);
      return client.verifyDomain(tenantId, domainId);
    },
    onSuccess: async (domain) => {
      setError(undefined);
      setBusyDomainId(null);
      toast({ tone: 'success', message: `${domain.host} is verified and live.` });
      await refresh();
    },
    onError: (cause: Error) => {
      setBusyDomainId(null);
      setError(cause.message);
    },
  });

  const remove = useMutation({
    mutationFn: (domainId: string) =>
      guard(() => client.removeDomain(tenantId, domainId), 'remove a hostname'),
    onSuccess: async () => {
      setError(undefined);
      toast({ tone: 'success', message: 'Domain removed.' });
      await refresh();
    },
    onError: (cause: Error) => {
      setError(cause.message);
    },
  });

  if (tenant.isPending || domains.isPending || settings.isPending) {
    return (
      <Card className="p-6">
        <Spinner label="Loading domains" />
      </Card>
    );
  }
  if (tenant.isError || domains.isError || settings.isError) {
    return <Alert tone="error">That tenant could not be loaded.</Alert>;
  }

  return (
    <DomainsTab
      domains={domains.data}
      platformDomain={settings.data.platformDomain}
      cnameTarget={settings.data.cnameTarget}
      error={error}
      busyDomainId={busyDomainId}
      readOnly={tenant.data.tenant.status === 'archived'}
      onAdd={(input) => {
        add.mutate(input);
      }}
      onVerify={(domainId) => {
        verify.mutate(domainId);
      }}
      onRemove={(domainId) => {
        remove.mutate(domainId);
      }}
    />
  );
}
